//////////////////////////////////////////////////////////////////////////////////////////////////////////////////

import { Stack, Typography } from "@mui/material";
import { MprcContainer } from "components";
import { AutoCompleteDataModel } from "components/MprcAutoComplete";
import { MprcDatePicker } from "components/MprcDatePicker";
import { MprcRequestSnackbarErrorReport } from "components/MprcRequestErrorReport";
import { MprcSwitch } from "components/MprcSwitch";
import { GetPublicationAdvancedOptionsDto, PublicationTagDto, TagUsage } from "generated/CoreApi_v1";
import { Formaters } from "lib/Formaters";
import LibUtils from "lib/LibUtils";
import { useCoreApi } from "providers/CoreApiProvider";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { TagAutoComplete } from "views/foundation/tag/components/TagAutoComplete";
import { IStickerBoxValidation, PublicationStickerBox } from "views/knowledge/publication/creation/components/PublicationStickerBox";

export interface IInstitutionalBoxValidation {
    period?: boolean;
}

interface IDataProps {
    advancedOptions: GetPublicationAdvancedOptionsDto;
    validation: IInstitutionalBoxValidation & IStickerBoxValidation;
}

interface IProps extends IDataProps {
    channelId?: string | null;
    allowInstitutionalPublisher: boolean;
    canCreateTags: boolean;
    onUpdateState: (data: IDataProps) => void;
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////

const DatePickerMaxDate = new Date('2100-01-01');
const DatePickerMinDate = new Date('1900-01-01');

export const PublicationAdvancedBox = (props: IProps) => {
    let {
        advancedOptions: { viewStickerUntil, viewPeriodEnd, viewPeriodStart, isInstitutional, sticker, tags },
        allowInstitutionalPublisher,
        validation,
        channelId,
        canCreateTags
    } = props;

    const { t } = useTranslation("knowledge");
    const api = useCoreApi();

    const [allowStickers, setAllowStickers] = useState<boolean>(false);
    const [enabled, setEnabled] = useState<boolean>(viewStickerUntil != null || viewPeriodEnd != null || viewPeriodStart != null || sticker?.stickerId != null || isInstitutional || tags.length > 0);

    ///
    function DatePickerValidRange(dt: Date) {
        return (dt != null && (dt < DatePickerMinDate || dt > DatePickerMaxDate));
    }

    ///
    const updateState = (data: Partial<GetPublicationAdvancedOptionsDto>) => {
        let fp: IDataProps = {
            advancedOptions: { ...props.advancedOptions, ...data },
            validation: { ...props.validation },
        };

        let { advancedOptions: { viewStickerUntil, viewPeriodEnd, viewPeriodStart, sticker }, validation } = fp;

        validation.period = validation.viewStickerUntil = true;

        if (
            (viewPeriodStart != null && !LibUtils.isValidDate(viewPeriodStart))
            || (viewPeriodEnd != null && !LibUtils.isValidDate(viewPeriodEnd))
            || (viewPeriodStart != null && viewPeriodEnd != null && viewPeriodStart > viewPeriodEnd)
            || (viewPeriodStart != null && DatePickerValidRange(viewPeriodStart))
            || (viewPeriodEnd != null && DatePickerValidRange(viewPeriodEnd))
        ) {
            validation.period = false;
        }

        if (
            (sticker && sticker.requiredDeadline)
            && (
                viewStickerUntil == null
                || !LibUtils.isValidDate(viewStickerUntil)
                || (viewStickerUntil != null && viewPeriodStart != null && viewPeriodStart > viewStickerUntil)
                || (viewStickerUntil != null && viewPeriodEnd != null && viewPeriodEnd < viewStickerUntil)
                || (viewStickerUntil != null && DatePickerValidRange(viewStickerUntil))
            )
        ) {
            validation.viewStickerUntil = false;
        }

        props.onUpdateState(fp);
    };

    ///
    useEffect(() => {
        async function checkAccess() {
            let allowStickers = false;

            if (channelId != null) {
                try {
                    const result = await api.v1.knowledgePublication.getAdvancedOptionsSettings({ channelId: channelId });
                    allowStickers = result.data.allowStickers;
                }
                catch {
                }
            }

            if (!allowStickers && sticker?.stickerId != null) {
                updateState({ sticker: null, viewStickerUntil: null });
            }

            setAllowStickers(allowStickers);
        }
        checkAccess();
    }, [channelId]);

    ///
    useEffect(() => {
        if (!allowInstitutionalPublisher && isInstitutional) {
            updateState({ isInstitutional: false });
        }
    }, []);

    ///
    return (
        <>
            <MprcContainer>
                <MprcSwitch
                    name="enabled"
                    checked={enabled}
                    onChange={(e, checked) => setEnabled(checked)}
                    value="enabled"
                    label={t("Publication.AdvancedBox.EnabledLabel")}
                />
            </MprcContainer>

            {
                enabled &&
                <>
                    <MprcContainer>

                        <Typography variant="button">{t("Publication.AdvancedBox.DisplayPeriodTitle")}</Typography>
                        <Stack direction="row" alignItems="center" spacing={4}>
                            <MprcDatePicker
                                autoFocus
                                label={Formaters.capitalize(t("Publication.AdvancedBox.StartRangeLabel"))}
                                value={viewPeriodStart}
                                maxDate={viewPeriodEnd || DatePickerMaxDate}
                                timePartHour="initialDate"
                                error={validation.period === false}
                                onChange={(date) => {
                                    updateState({ viewPeriodStart: date });
                                }}
                            />

                            <MprcDatePicker
                                label={Formaters.capitalize(t("Publication.AdvancedBox.EndRangeLabel"))}
                                minDate={viewPeriodStart || DatePickerMinDate}
                                value={viewPeriodEnd}
                                timePartHour="endDate"
                                error={validation.period === false}
                                onChange={(date) => updateState({ viewPeriodEnd: date })}
                            />


                            {
                                allowInstitutionalPublisher &&

                                <MprcSwitch
                                    name="institutional"
                                    checked={isInstitutional}
                                    onChange={(e, checked) => updateState({ isInstitutional: checked })}
                                    value="institutional"
                                    label={t("Publication.AdvancedBox.InstitutionalLabel")}
                                    helperText={t("Publication.AdvancedBox.InstitutionalHelperText")}
                                />

                            }
                        </Stack>
                    </MprcContainer>

                    <MprcContainer margin="large">
                        <MprcContainer>
                            <Typography variant="button">{t("Publication.AdvancedBox.TagTitle")}</Typography>
                            <TagAutoComplete
                                placeholder={canCreateTags ? t("Publication.AdvancedBox.SelectTagsPlaceholder") : t("Publication.AdvancedBox.CreateAndSelectTagsPlaceholder")}
                                usage={TagUsage.Publication}
                                selectedItems={
                                    tags
                                        ? tags.map((x) => ({ key: x.tagId, label: x.tagName, canDelete: true } as AutoCompleteDataModel))
                                        : []
                                }
                                onChange={
                                    (items: AutoCompleteDataModel[]) => updateState({ tags: items.map((x) => ({ tagId: x.key, tagName: x.label } as PublicationTagDto)) })
                                }
                                canCreate={canCreateTags}
                                onError={(error) => MprcRequestSnackbarErrorReport(error)}
                                helperText={t("Publication.AdvancedBox.TagHelperText")}
                            />
                        </MprcContainer>
                    </MprcContainer>


                    {
                        allowStickers &&
                        <PublicationStickerBox
                            advancedOptions={props.advancedOptions}
                            enabled={true}
                            onUpdateState={({ sticker, viewStickerUntil }) => updateState({ sticker, viewStickerUntil })}
                            validation={validation}
                        />
                    }
                </ >
            }
        </>
    );
};
